import { useSearchParams, useNavigate, Navigate } from "react-router-dom";
import { Check, ExternalLink, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, PageHeader, SectionTitle } from "@/components/common";
import { ProfileForm, SlugForm, BillingForm, HoursForm } from "@/components/pro/GarageForms";
import { ServicesManager } from "@/components/pro/ServicesManager";
import { EmployeesManager } from "@/components/pro/EmployeesManager";
import { PublicLinkCard } from "@/components/pro/PublicLinkCard";
import { STEP_ORDER } from "@/pages/pro/ProDashboard";
import { useGarage } from "@/context/GarageContext";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { t } from "@/i18n";

function Stepper({ steps, current, onGo }) {
  return (
    <ol className="flex gap-2 overflow-x-auto pb-2" data-testid="onboarding-stepper">
      {STEP_ORDER.map((key, i) => {
        const done = steps.find((s) => s.key === key)?.done;
        const active = i + 1 === current;
        return (
          <li key={key} className="shrink-0">
            <button
              type="button"
              onClick={() => onGo(i + 1)}
              data-testid={`onboarding-step-${i + 1}`}
              className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors duration-200 ${
                active ? "border-[#0A2540] bg-[#0A2540] text-white" : done ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
              }`}
            >
              <span className={`flex h-5 w-5 items-center justify-center rounded-full ${active ? "bg-white/20" : done ? "bg-emerald-600 text-white" : "bg-slate-100"}`}>
                {done && !active ? <Check className="h-3 w-3" /> : i + 1}
              </span>
              {t(`setup.steps.${key}`)}
            </button>
          </li>
        );
      })}
    </ol>
  );
}

export default function Onboarding() {
  const { garage, setGarage, isManager, readOnly } = useGarage();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const steps = garage.setup.steps;
  const firstOpen = STEP_ORDER.findIndex((k) => !steps.find((s) => s.key === k)?.done) + 1 || 8;
  const current = Math.min(8, Math.max(1, Number(params.get("step")) || firstOpen));
  const go = (n) => setParams({ step: String(n) });

  if (!isManager) return <Navigate to="/pro" replace />;

  const complete = async (stepKey, n) => {
    try {
      const { data } = await api.post("/garage/onboarding/step", { step: stepKey });
      setGarage(data);
      go(n + 1);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const cont = t("onboarding.saveContinue");
  const body = {
    1: (
      <div className="space-y-4" data-testid="onboarding-manager-step">
        <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
          <UserCheck className="h-5 w-5" /> {t("onboarding.managerDone", { name: user.name, email: user.email })}
        </div>
        <Button onClick={() => go(2)} data-testid="onboarding-next-button">{t("onboarding.next")}</Button>
      </div>
    ),
    2: <SlugForm withName submitLabel={cont} onSaved={() => complete("garage", 2)} />,
    3: <ProfileForm submitLabel={cont} onSaved={() => complete("branding", 3)} />,
    4: <BillingForm submitLabel={cont} onSaved={() => complete("billing", 4)} />,
    5: (
      <div className="space-y-10">
        <HoursForm submitLabel={t("onboarding.saveHours")} />
        <div>
          <SectionTitle hint={t("services.phaseNote")}>{t("onboarding.services")}</SectionTitle>
          <ServicesManager />
        </div>
        <Button disabled={readOnly} onClick={() => complete("hours_services", 5)} data-testid="onboarding-hours-continue-button">{t("onboarding.continue")}</Button>
      </div>
    ),
    6: (
      <div className="space-y-6">
        <EmployeesManager />
        <Button disabled={readOnly} onClick={() => complete("employees", 6)} data-testid="onboarding-employees-continue-button">{t("onboarding.continue")}</Button>
      </div>
    ),
    7: (
      <div className="space-y-4" data-testid="onboarding-preview-step">
        <p className="text-sm text-slate-600">{t("onboarding.previewText")}</p>
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <iframe title={t("onboarding.previewTitle")} src={`/g/${garage.slug}?preview=1`} className="h-[560px] w-full" data-testid="portal-preview-iframe" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" asChild data-testid="open-preview-new-tab"><a href={`/g/${garage.slug}?preview=1`} target="_blank" rel="noreferrer">{t("onboarding.openPreview")} <ExternalLink className="ml-2 h-4 w-4" /></a></Button>
          <Button disabled={readOnly} onClick={() => complete("preview", 7)} data-testid="onboarding-preview-continue-button">{t("onboarding.looksGood")}</Button>
        </div>
      </div>
    ),
    8: (
      <div className="space-y-6">
        <PublicLinkCard />
        {garage.setup.complete && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900" data-testid="onboarding-complete-message">
            {t("onboarding.allDone")}
          </div>
        )}
        <Button variant={garage.setup.complete ? "default" : "outline"} onClick={() => navigate("/pro")} data-testid="onboarding-finish-button">{t("onboarding.toDashboard")}</Button>
      </div>
    ),
  };

  return (
    <div>
      <PageHeader eyebrow={t("onboarding.eyebrow", { n: current })} title={t(`setup.steps.${STEP_ORDER[current - 1]}`)} subtitle={t(`onboarding.hint.${STEP_ORDER[current - 1]}`)} />
      <Stepper steps={steps} current={current} onGo={go} />
      <Card className="mt-6" key={current}>{body[current]}</Card>
    </div>
  );
}
